import { CATEGORY_EMOJI, type Category, coerceEmoji, itemEmoji } from "@larder/shared";
import { useState } from "react";

/**
 * Choosing the glyph for an item.
 *
 * The model's guess is usually right and this is usually untouched — so the
 * control is built for CORRECTING, not for choosing from scratch. The current
 * glyph is the button; a short palette covers the common misses; and a text box
 * takes anything at all, because the phone's own emoji keyboard is a better
 * picker than any grid this app could ship.
 *
 * An edit here writes to the ITEM, never to the global product cache. One
 * household deciding their rajma is 🫘 must not repaint every other kitchen in
 * the country — the cache is shared, and shared state is not the place for
 * taste. See docs/plans/active/0003-enrichment.md.
 */

/**
 * The palette. The ten category glyphs, plus the near misses people actually
 * reach for — the things a category emoji is too coarse to say.
 */
const PALETTE: readonly string[] = [
  ...Object.values(CATEGORY_EMOJI),
  "🍚",
  "🍜",
  "🥚",
  "🧈",
  "🧀",
  "🍫",
  "☕",
  "🫘",
  "🌶️",
  "🧄",
  "🍎",
  "🍌",
  "🥔",
  "🧅",
  "🍅",
  "🐟",
  "🍗",
  "🧼",
  "🧻",
  "🍯",
];

export function EmojiField({
  value,
  category,
  onChange,
}: {
  value: string | null;
  /** Drives the fallback, so the button is never empty. */
  category: Category | null;
  onChange: (next: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const shown = itemEmoji(value, category);

  return (
    <div className="emoji-field">
      <button
        type="button"
        className="emoji-current"
        onClick={() => setOpen((v) => !v)}
        aria-label="Change the icon"
        aria-expanded={open}
        data-testid="emoji-button"
      >
        <span aria-hidden="true">{shown}</span>
      </button>

      {open && (
        <div className="emoji-pop">
          <div className="emoji-grid">
            {PALETTE.map((e) => (
              <button
                key={e}
                type="button"
                className={`emoji-pick ${value === e ? "on" : ""}`}
                onClick={() => {
                  onChange(e);
                  setOpen(false);
                }}
                // The glyph IS the label here — unlike everywhere else in the
                // app, this button carries no text beside it, so it must name
                // itself or it is a row of unlabelled buttons.
                aria-label={`Use ${e}`}
                data-testid={`emoji-${e}`}
              >
                {e}
              </button>
            ))}
          </div>

          <div className="inline-add">
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Or type any emoji"
              aria-label="Type an emoji"
              maxLength={8}
              data-testid="emoji-typed"
            />
            <button
              type="button"
              className="chip"
              // Same guard the model's answers go through, so a pasted word
              // cannot end up rendering as text where a glyph belongs.
              disabled={!coerceEmoji(typed)}
              onClick={() => {
                const ok = coerceEmoji(typed);
                if (!ok) return;
                onChange(ok);
                setTyped("");
                setOpen(false);
              }}
              data-testid="emoji-use-typed"
            >
              Use
            </button>
            {value && (
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  onChange(null);
                  setOpen(false);
                }}
                data-testid="emoji-reset"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
